import '../serverSettings.scss'
import Input from 'components/FormComponents/Input/Input'
import Select from 'components/FormComponents/Select/Select'
import { InputTypes, SelectionTypes } from '../../../../lib/enums'
import Accordion from 'components/Accordion/Accordion'
import AccordionItem from 'components/Accordion/AccordionItem'
import Checkbox from 'components/Checkbox/Checkbox'
import ServerSettingsStorageSetup from '../ServerSettingsStorageSetup'
import { useGenericServerSettingsData } from './GenericServerSettingsInitialization'
import { useEffect } from 'react'

const GenericServerSettings = ({
  formData,
  serverSettings,
  camerasAmount,
  handleXProtectPlatformChange,
  handleValueChange,
  handleArchivingValueChange,
  handleStorageSetupValueChange,
  handleSupportedCamerasValueChange,
  hardwareTypeSelect,
  setHasManagementServerValidationError
}: GenericServerSettingsProps) => {
  const {
    genericformData,
    genericAddManagementServer,
    xProtectPlatforms,
    genericXProtectVersion,
    genericXProtectVersionCameraMaxQty,
    genericXProtectVersionCameraQty,
    genericSystemOverhead,
    genericConcurrentSmartClientStreams,
    machineTypes,
    genericMachineType,
    archiveFrequencies,
    genericArchiveFrequency,
    genericServerDiskQuantity,
    raiD1ForOSOptions,
    genericRaiD1ForOSOption,
    serverDiskQuantities,
    supportedCameras,
    genericSupportedCamera,
    genericLiveDatase,
    hasGenericArchiving,
    genericArchiveDatabase,
    isNumberOfCamerasExceedingManagementServerSelection,
    handleGenericXProtectPlatformChange,
    handleGenericValueChange,
    handleGenericArchivingValueChange,
    handleGenericStorageSetupValueChange,
    handleGenericSupportedCamerasValueChange
  } = useGenericServerSettingsData(
    formData,
    serverSettings,
    camerasAmount,
    handleXProtectPlatformChange,
    handleValueChange,
    handleArchivingValueChange,
    handleStorageSetupValueChange,
    handleSupportedCamerasValueChange
  )

  useEffect(() => {
    setHasManagementServerValidationError?.(isNumberOfCamerasExceedingManagementServerSelection)
  }, [isNumberOfCamerasExceedingManagementServerSelection, setHasManagementServerValidationError])

  return (
    <>
      <div className="row align-items-center mt-6 mb-n8">
        {hardwareTypeSelect && hardwareTypeSelect()}
        <Select
          id="xsd__e2e-xprotect-version"
          selectionType={SelectionTypes.Number}
          className="col-3"
          defaultOption={{ label: 'Select XProtect Version', value: '' }}
          options={xProtectPlatforms}
          handleDataChange={handleGenericXProtectPlatformChange()}
          initialValue={genericXProtectVersion.toString()}
        />
        {/* <Checkbox className='col-2' id="failover-settings" label="Add Failover Server" handleChange={handleFailoverCheckboxChange} initialValue={isFailoverEnabled} isDisabled={!optionsSetup?.xProtectPlatform?.failoverSupport} /> */}
        <Checkbox
          className="col-3 server-settings-checkbox"
          id="xsd__e2e-add-management-server"
          label="Add Management Server"
          handleChange={handleGenericValueChange(['genericServerSettings', 'addManagementServer'])}
          initialValue={genericAddManagementServer}
          isDisabled={genericXProtectVersion === 3}
        />
        {genericXProtectVersion === 3 && <div className="col-8 offset-3 mt-4 ml-1 msds-text-body-3 msds-text-gray-10">XProtect Express+ only supports up to 48 cameras</div>}
      </div>
      <Accordion>
        <AccordionItem title="Recording Server Settings" forceExpand>
          <div className="row justify-content-start mb-4">
            <div className="server-settings__header msds-text-body-0-bold col-12 my-4">Main Settings</div>
            <Input
              id="xsd__e2e-main-settings-system-overhead"
              type={InputTypes.Number}
              className="col-3 ml-4"
              label="System overhead (%)"
              isSmall
              handleDataChange={handleGenericValueChange(['genericServerSettings', 'mainSettings', 'systemOverhead'], genericSystemOverhead)}
              initialValue={genericSystemOverhead.toString()}
              maximumNumber={500}
              minimumNumber={0}
            />
            <Input
              id="xsd__e2e-main-settings-max-camera-per-server"
              type={InputTypes.Number}
              className="col-3"
              label="Max cameras per server"
              isSmall
              handleDataChange={handleGenericValueChange(['genericServerSettings', 'mainSettings', 'maxCamerasPerServer'], genericXProtectVersionCameraQty)}
              initialValue={genericXProtectVersionCameraQty.toString()}
              maximumNumber={genericXProtectVersionCameraMaxQty}
              minimumNumber={1}
            />
            <Input
              id="xsd__e2e-main-settings-concurrent-smart-client-streams"
              type={InputTypes.Number}
              className="col-3"
              label="Concurrent Smart Client Streams"
              isSmall
              handleDataChange={handleGenericValueChange(['genericServerSettings', 'mainSettings', 'concurrentSmartClientStreams'], genericConcurrentSmartClientStreams)}
              initialValue={genericConcurrentSmartClientStreams.toString()}
              maximumNumber={9999}
              minimumNumber={0}
            />
          </div>
          <div className="row justify-content-start mb-4 align-items-center">
            <div className="server-settings__header msds-text-body-0-bold col-12 my-4">Advanced Settings</div>
            <Select
              id="xsd__e2e-advanced-settings-machine-type"
              selectionType={SelectionTypes.Number}
              className="col-3 ml-4 dropdownlist__machine-type"
              defaultOption={{ label: 'Machine Type', value: '' }}
              options={machineTypes}
              isSmall
              handleDataChange={handleGenericValueChange(['genericServerSettings', 'advancedSettings', 'machineType'])}
              initialValue={genericMachineType.toString()}
            />
            <Select
              id="xsd__e2e-advanced-settings-max-disks-per-server"
              selectionType={SelectionTypes.Number}
              className="col-3"
              defaultOption={{ label: 'Max Disks Per Server', value: '' }}
              options={serverDiskQuantities}
              isSmall
              handleDataChange={handleGenericValueChange(['genericServerSettings', 'advancedSettings', 'serverDiskQuantity'])}
              initialValue={genericServerDiskQuantity.toString()}
            />
            <Select
              id="xsd__e2e-advanced-settings-raid-os"
              selectionType={SelectionTypes.Boolean}
              className="col-3"
              defaultOption={{ label: 'Raid OS', value: '' }}
              options={raiD1ForOSOptions}
              isSmall
              handleDataChange={handleGenericValueChange(['genericServerSettings', 'advancedSettings', 'raidOS'])}
              initialValue={genericRaiD1ForOSOption.toString()}
            />
            {
              <Checkbox
                id="xsd__e2e-advanced-settings-archiving"
                className="col-2 server-settings-checkbox"
                label="Archiving"
                handleChange={handleGenericArchivingValueChange(['genericServerSettings', 'advancedSettings', 'archiving'])}
                initialValue={hasGenericArchiving}
              />
            }
          </div>
          <ServerSettingsStorageSetup
            header="Live Database"
            handleDataChange={handleGenericStorageSetupValueChange(['genericServerSettings', 'advancedSettings', 'liveDatabase'])}
            formData={genericformData}
            storageSetup={genericLiveDatase}
          />
          {hasGenericArchiving && (
            <ServerSettingsStorageSetup
              header="Archiving"
              handleDataChange={handleGenericStorageSetupValueChange(['genericServerSettings', 'advancedSettings', 'archiveDatabase'])}
              formData={genericformData}
              storageSetup={genericArchiveDatabase}>
              <Select
                id="xsd__e2e-archiving-archive-frequency"
                selectionType={SelectionTypes.Number}
                className="col-3 mb-4"
                defaultOption={{ label: 'Archive Frequency (times per day)', value: '' }}
                options={archiveFrequencies}
                isSmall
                handleDataChange={handleGenericValueChange(['genericServerSettings', 'advancedSettings', 'archiveFrequency'])}
                initialValue={genericArchiveFrequency.toString()}
              />
            </ServerSettingsStorageSetup>
          )}
        </AccordionItem>
        <AccordionItem title="Managment Server Settings" isDisabled={!genericAddManagementServer} forceExpand={genericAddManagementServer}>
          <div className="row justify-content-start mb-4">
            <Select
              id="xsd__e2e-managment-server-settings-supported-cameras"
              selectionType={SelectionTypes.Number}
              className="col-3"
              defaultOption={{ label: 'Supported Cameras', value: '' }}
              options={supportedCameras}
              isSmall
              handleDataChange={handleGenericSupportedCamerasValueChange()}
              initialValue={genericSupportedCamera.toString()}
            />
            {isNumberOfCamerasExceedingManagementServerSelection && (
              <div className="msds-text-body-3 xsd-proposals-pdf__card-info mt-2 msds-text-danger-red-1">Please note that the sum of carmeras you have added exceeds your selection.</div>
            )}
          </div>
        </AccordionItem>
      </Accordion>
    </>
  )
}

export default GenericServerSettings

type GenericServerSettingsProps = {
  formData
  serverSettings
  camerasAmount
  handleXProtectPlatformChange
  handleValueChange
  handleArchivingValueChange
  handleStorageSetupValueChange
  handleSupportedCamerasValueChange
  hardwareTypeSelect
  setHasManagementServerValidationError?: (hasError: boolean) => void
}
